import CustomHTMLElement from '@onpace/onspace-core/components/html_element'

import client from '@onpace/onspace-core/components/client'
import translation from '@onpace/onspace-core/components/translations'

/// An element which handles address autocomplete using geocoding services.
///
/// This works by listening for input in the autocomplete field, fetching suggestions
/// from the geocoding API, and populating the address fields when a suggestion is selected.
export default class AddressAutocomplete extends CustomHTMLElement {
  /// Sets up the address autocomplete element.
  runConstructor() {
    super.runConstructor()

    this.countryFilter = this.getAttribute('data-country')
    this.autocompleteUrl = this.getAttribute('data-autocomplete-url')
    this.detailUrl = this.getAttribute('data-detail-url')

    this._isLoading = false
    this.autocompleteResults = null

    this.autocompleteInput = this.querySelector('input[data-autocomplete-input]')
    this.autocompleteInput.addEventListener('focus', this.inputFocused.bind(this))
    this.autocompleteInput.addEventListener('blur', this.inputBlurred.bind(this))
    this.autocompleteInput.addEventListener('input', this.inputChanged.bind(this))

    this.loadingIcon = SVGElement.createOnspaceSpritemapSvg('onspace/icon_loading')
    this.loadingIcon.classList.add('onspace-autocomplete__indicator')
    this.appendChild(this.loadingIcon)

    this.resultsElement = this.querySelector('.onspace-autocomplete__results')
  }

  ////////// Loading

  /// Indicates whether the element is currently loading.
  get isLoading() {
    return this._isLoading
  }

  /// Updates the element's loading state.
  set isLoading(newValue) {
    this._isLoading = newValue
    this.showLoading(newValue)
  }

  /// Displays or hides the loading indicator.
  showLoading(enabled) {
    this.classList.toggle('onspace-autocomplete--loading', enabled)
  }

  ////////// Results

  /// Fetches autocomplete suggestions from the API.
  async updateResults() {
    this.errorMessage = null

    const query = this.autocompleteInput.value.trim()
    if (query.length < 3) {
      this.autocompleteResults = null
      this.displayResults()
      this.isLoading = false
      return
    }

    this.isLoading = true

    try {
      const response = await client.GET(this.autocompleteUrl, { params: { query, country: this.countryFilter } })
      const content = await response.content()
      this.autocompleteResults = content.places
    } catch (error) {
      this.errorMessage = translation('onspace.field.street_address.subfield.autocomplete.error')
    } finally {
      this.isLoading = false
      this.displayResults()
    }

    const latestQuery = this.autocompleteInput.value.trim()
    if (query !== latestQuery) { this.updateResults() }
  }

  /// Displays the suggestions in the results container.
  displayResults() {
    this.classList.add('onspace-autocomplete--visible')

    if (typeof this.errorMessage === 'string') {
      this.resultsElement.innerHTML = `<div class="onspace-autocomplete__results__error">${this.errorMessage}</div>`
    } else if (this.autocompleteResults === null) {
      this.resultsElement.innerHTML = `<div class="onspace-autocomplete__results__message">${translation('onspace.field.street_address.subfield.autocomplete.placeholder')}</div>`
    } else if (this.autocompleteResults.length === 0) {
      this.resultsElement.innerHTML = `<div class="onspace-autocomplete__results__message">${translation('onspace.field.street_address.subfield.autocomplete.empty')}</div>`
    } else {
      this.resultsElement.innerHTML = ''
      this.autocompleteResults.forEach((result) => {
        const resultElement = document.createElement('div')
        resultElement.classList.add('onspace-autocomplete__results__result')
        resultElement.innerText = result.address
        resultElement.addEventListener('click', (_event) => {
          this.selectAutocompleteResult(result)
        })

        this.resultsElement.appendChild(resultElement)
      })
    }
  }

  /// Hides the results container.
  hideResults() {
    this.classList.remove('onspace-autocomplete--visible')
  }

  ////////// Selection

  /// Handles selecting a suggestion
  async selectAutocompleteResult(result) {
    this.autocompleteInput.value = ''
    this.autocompleteResults = []

    this.showLoading(true)

    const response = await client.GET(this.detailUrl, { params: { id: result.id } })
    const content = await response.content()
    this.populateAddressFields(content.place)

    this.showLoading(false)
  }

  /// Populates the address fields from the given detailed place.
  populateAddressFields(place) {
    const addressInputs = this.querySelectorAll('input[name]')
    addressInputs.forEach((input) => {
      const inputAttribute = input.name.replace(/^.*\[([a-z_]+)\]$/, '$1')
      if (input.type === 'radio') {
        if (place[inputAttribute] === input.value) { input.click() }
      } else {
        input.value = place[inputAttribute]
      }
    })
  }

  ////////// Events

  /// Responds to the input element becoming the focus.
  ///
  /// This will show the suggestions element.
  inputFocused(_event) {
    this.displayResults()
  }

  /// Responds to the input element resigning the focus.
  ///
  /// This will hide the suggestions element after a short delay, to ensure that the results can still be clicked.
  inputBlurred(_event) {
    setTimeout(() => { this.hideResults() }, 200)
  }

  /// Handles input changes with debouncing
  /// Responds to changes in the input element.
  ///
  /// This causes the suggestions to be updated, after a short delay to group multiple changes together.
  inputChanged(_event) {
    if (this.isLoading) { return }

    if (this.debounceTimmer) {
      window.clearTimeout(this.debounceTimmer)
      this.debounceTimmer = null
    }

    this.debounceTimmer = window.setTimeout(this.updateResults.bind(this), 300)
    this.showLoading(true)
  }
}

customElements.define('address-autocomplete', AddressAutocomplete)
